<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import WootDatePicker from 'dashboard/components/ui/DatePicker/DatePicker.vue';
import FilterDropdown from './FilterDropdown.vue';
import HourlyTrendChart from './HourlyTrendChart.vue';
import {
  useReportFilterState,
  fmtReportDate,
} from './reportFilterState';
/* global axios */

// 留电来源报表: 按归属者 × 来源URL 分组统计已留联(留电)联系人数量 + 当月每日曲线。
// 后端 GET /api/v1/accounts/:id/contacts/lead_source_report
//   params: owners[](多选,空=全部) sources[](多选来源,空=全部) domains[](多选域名)
//           since/until(contact_saved_at 北京时区日期)
//   resp:   { rows, owner_options, source_options, domain_options, domains,
//             total, conversations, conversations_total, daily, month_total, month_label }
// daily 固定当月(不受日期范围筛选影响), 受归属者/来源/域名筛选影响, 与主表同口径。
const route = useRoute();

const rows = ref([]);
const ownerOptions = ref([]);
const sourceOptions = ref([]);
const domainOptions = ref([]);
const domainRows = ref([]);
const total = ref(0);
const conversationsTotal = ref(0);
const daily = ref([]);
const monthTotal = ref(0);
const monthLabel = ref('');
const prevDaily = ref([]);
const prevMonthTotal = ref(0);
const prevMonthLabel = ref('');
const todayHourly = ref([]);
const todayHourlyTotal = ref(0);
const todayDate = ref('');
const yesterdayHourly = ref([]);
const yesterdayHourlyTotal = ref(0);
const yesterdayDate = ref('');
const lastweekHourly = ref([]);
const lastweekHourlyTotal = ref(0);
const lastweekDate = ref('');
const loading = ref(false);
const loaded = ref(false);

// 筛选状态存模块级单例: 离开报表页再回来(组件重挂载)恢复上次筛选,
// 根治"自定义日期选好后跳回今天"(定制 2026-08-26)
const {
  selectedOwners,
  selectedSources,
  selectedDomains,
  customDateRange,
  selectedRangeType,
  since,
  until,
} = useReportFilterState();

// 日期格式化(北京时区 wall-clock 字段)
const fmtDate = fmtReportDate;

// 排序: 默认留电数降序; 点表头可切换归属者/来源/来源URL 升降序
const sortKey = ref('count');
const sortDir = ref('desc');

// 明细表 Tab: 按来源URL(搜索引擎维度) / 按网站域名(widget 嵌入页维度), 二选一切换减少页面堆叠
const activeTab = ref('source');

const accountId = computed(() => route.params.accountId);

// 筛选下拉选项: 统一 {value,label,count} 形态喂给 FilterDropdown
const ownerOpts = computed(() =>
  ownerOptions.value.map(name => ({ value: name, label: name }))
);
const sourceOpts = computed(() =>
  sourceOptions.value.map(o => ({ value: o.key, label: o.label, count: o.count }))
);
const domainOpts = computed(() =>
  domainOptions.value.map(o => ({ value: o.domain, label: o.domain, count: o.count }))
);

// 下拉多选变更(不可变更新数组, 立即重查)
const onOwnersChange = val => {
  selectedOwners.value = val;
  fetchReport();
};
const onSourcesChange = val => {
  selectedSources.value = val;
  fetchReport();
};
const onDomainsChange = val => {
  selectedDomains.value = val;
  fetchReport();
};

// 日历选择器选好范围后 → 回写 v-model(日历按钮显示与查询条件一致, 组件重挂载不跳回今天)
// 并填入 since/until 立即查询
const onDateRange = value => {
  const [start, end, rangeType] = value || [];
  since.value = start ? fmtDate(new Date(start)) : '';
  until.value = end ? fmtDate(new Date(end)) : '';
  if (start && end) customDateRange.value = [new Date(start), new Date(end)];
  if (rangeType) selectedRangeType.value = rangeType;
  fetchReport();
};

const fetchReport = async () => {
  loading.value = true;
  try {
    const params = {};
    if (selectedOwners.value.length) params.owners = selectedOwners.value;
    if (selectedSources.value.length) params.sources = selectedSources.value;
    if (selectedDomains.value.length) params.domains = selectedDomains.value;
    if (since.value) params.since = since.value;
    if (until.value) params.until = until.value;
    const { data } = await axios.get(
      `/api/v1/accounts/${accountId.value}/contacts/lead_source_report`,
      { params }
    );
    rows.value = data.rows || [];
    ownerOptions.value = data.owner_options || [];
    sourceOptions.value = data.source_options || [];
    domainOptions.value = data.domain_options || [];
    domainRows.value = data.domains || [];
    total.value = data.total || 0;
    conversationsTotal.value = data.conversations_total || 0;
    daily.value = data.daily || [];
    monthTotal.value = data.month_total || 0;
    monthLabel.value = data.month_label || '';
    prevDaily.value = data.prev_daily || [];
    prevMonthTotal.value = data.prev_month_total || 0;
    prevMonthLabel.value = data.prev_month_label || '';
    todayHourly.value = data.today_hourly || [];
    todayHourlyTotal.value = data.today_hourly_total || 0;
    todayDate.value = data.today_date || '';
    yesterdayHourly.value = data.yesterday_hourly || [];
    yesterdayHourlyTotal.value = data.yesterday_hourly_total || 0;
    yesterdayDate.value = data.yesterday_date || '';
    lastweekHourly.value = data.lastweek_hourly || [];
    lastweekHourlyTotal.value = data.lastweek_hourly_total || 0;
    lastweekDate.value = data.lastweek_date || '';
    loaded.value = true;
  } catch {
    useAlert('报表加载失败', 'error');
  } finally {
    loading.value = false;
  }
};

const sortedRows = computed(() => {
  const list = [...rows.value];
  const dir = sortDir.value === 'asc' ? 1 : -1;
  return list.sort((a, b) => {
    let cmp = 0;
    if (sortKey.value === 'count') cmp = a.count - b.count;
    else if (sortKey.value === 'owner') cmp = a.owner.localeCompare(b.owner, 'zh');
    else if (sortKey.value === 'source')
      cmp = (a.source_label || '').localeCompare(b.source_label || '', 'zh');
    else cmp = a.source_url.localeCompare(b.source_url);
    return cmp * dir;
  });
});

const setSort = key => {
  if (sortKey.value === key) {
    sortDir.value = sortDir.value === 'asc' ? 'desc' : 'asc';
  } else {
    sortKey.value = key;
    sortDir.value = key === 'count' ? 'desc' : 'asc';
  }
};

const sortArrow = key =>
  sortKey.value === key ? (sortDir.value === 'asc' ? ' ↑' : ' ↓') : '';

// 按网站域名明细合计
const domainTotal = computed(() =>
  domainRows.value.reduce((sum, r) => sum + r.count, 0)
);

// ---------------- 当月每日留电曲线(叠加上月对比) ----------------
// 本月曲线固定当月(北京时区) 1号~今天; 上月曲线整月。两条按"日"对齐共用 x 轴,
// 无数据的天补 0(后端只回有留电的天)。X 轴总长取两月天数较大者(如 9月1日时
// 本月只有1个点, 上月31个点, 轴仍铺满31天)。
const bjNow = () => new Date(Date.now() + 8 * 3600000);

const chartData = computed(() => {
  const bj = bjNow();
  const monthPrefix = `${bj.getUTCFullYear()}-${String(bj.getUTCMonth() + 1).padStart(2, '0')}-`;
  const counts = {};
  daily.value.forEach(r => {
    counts[r.date] = r.count;
  });
  const out = [];
  for (let d = 1; d <= bj.getUTCDate(); d += 1) {
    const key = `${monthPrefix}${String(d).padStart(2, '0')}`;
    out.push({ day: d, date: key, count: counts[key] || 0 });
  }
  return out;
});

// 上月每日(prev_daily 的日期取"日"号, 1号~月末)
const prevData = computed(() => {
  const bj = bjNow();
  // 上月总天数: 当前月0-based月份 m, Date(y, m, 0)=上月最后一天
  const prevDays = new Date(Date.UTC(bj.getUTCFullYear(), bj.getUTCMonth(), 0)).getUTCDate();
  const counts = {};
  prevDaily.value.forEach(r => {
    counts[Number(r.date.slice(8, 10))] = r.count;
  });
  const out = [];
  for (let d = 1; d <= prevDays; d += 1) {
    out.push({ day: d, count: counts[d] || 0 });
  }
  return out;
});

// 当月日均(留电总数 / 已过天数)
const monthDailyAvg = computed(() => {
  if (!chartData.value.length) return 0;
  return Math.round((monthTotal.value / chartData.value.length) * 10) / 10;
});

// 图表几何: viewBox 固定, 宽度 100% 自适应缩放
const CHART_W = 800;
const CHART_H = 240;
const PAD = { l: 40, r: 16, t: 14, b: 26 };
const plotW = CHART_W - PAD.l - PAD.r;
const plotH = CHART_H - PAD.t - PAD.b;

// x 轴覆盖天数 = 本月已过天数与上月总天数的较大者
const xTotal = computed(() =>
  Math.max(chartData.value.length, prevData.value.length)
);

// y 轴整洁上限(1/2/2.5/5×10^k), 保证刻度是好读的整数; 两月取共同上限才可比
const niceCeil = v => {
  if (v <= 4) return 4;
  const p = 10 ** Math.floor(Math.log10(v));
  return [1, 2, 2.5, 5, 10].find(k => k * p >= v) * p;
};
const yMax = computed(() =>
  niceCeil(
    Math.max(
      1,
      ...chartData.value.map(p => p.count),
      ...prevData.value.map(p => p.count)
    )
  )
);
const xAt = i =>
  xTotal.value <= 1
    ? PAD.l + plotW / 2
    : PAD.l + (i / (xTotal.value - 1)) * plotW;
const yAt = v => PAD.t + plotH - (v / yMax.value) * plotH;

const linePath = computed(() =>
  chartData.value
    .map((p, i) => `${i === 0 ? 'M' : 'L'}${xAt(i).toFixed(1)},${yAt(p.count).toFixed(1)}`)
    .join('')
);
const prevLinePath = computed(() =>
  prevData.value
    .map((p, i) => `${i === 0 ? 'M' : 'L'}${xAt(i).toFixed(1)},${yAt(p.count).toFixed(1)}`)
    .join('')
);
const areaPath = computed(() => {
  if (!chartData.value.length) return '';
  const last = chartData.value.length - 1;
  const baseline = (PAD.t + plotH).toFixed(1);
  return `${linePath.value}L${xAt(last).toFixed(1)},${baseline}L${xAt(0).toFixed(1)},${baseline}Z`;
});

// y 轴刻度(0/25%/50%/75%/100%), 数值加千分位
const yTicks = computed(() =>
  [0, 0.25, 0.5, 0.75, 1].map(f => ({
    y: yAt(f * yMax.value),
    label: Math.round(f * yMax.value).toLocaleString(),
  }))
);
// x 轴日期标签稀疏化(约 12 个), 文字用天号
const xLabelStep = computed(() =>
  Math.max(1, Math.ceil(xTotal.value / 12))
);

// 日环比: 本月第 i 天 vs 前一天; 1号的前一天取上月最后一天(跨月衔接)
const dayDelta = i => {
  const cur = chartData.value[i];
  if (!cur) return null;
  const prevDay = i === 0
    ? prevData.value[prevData.value.length - 1]
    : chartData.value[i - 1];
  if (!prevDay) return null;
  const diff = cur.count - prevDay.count;
  return {
    diff,
    pct: prevDay.count > 0 ? Math.round((diff / prevDay.count) * 100) : null,
  };
};
// 环比文案: 持平 / +12 / +12（+15%）; 原始hex避开自定义调色板缺色坑
const fmtDelta = d => {
  if (!d || d.diff === 0) return '持平';
  const sign = d.diff > 0 ? '+' : '';
  const pct = d.pct !== null ? `（${d.pct > 0 ? '+' : ''}${d.pct}%）` : '';
  return `${sign}${d.diff}${pct}`;
};
// 图例常显: 最新一天的日环比
const latestDelta = computed(() =>
  (chartData.value.length ? dayDelta(chartData.value.length - 1) : null)
);

// 悬停: 十字线 + 高亮点; 命中区是整个绘图区(比 2px 折线大得多)
// 悬停索引按 x 轴总天数(两月较大者), 同一天可能只有上月有值(如1号本月还没到)
const hoverIndex = ref(-1);
const onChartMove = e => {
  const n = xTotal.value;
  if (!n) return;
  const rect = e.currentTarget.getBoundingClientRect();
  const vx = ((e.clientX - rect.left) * CHART_W) / rect.width;
  const idx = Math.round(((vx - PAD.l) / plotW) * (n - 1));
  hoverIndex.value = Math.min(n - 1, Math.max(0, idx));
};
const onChartLeave = () => {
  hoverIndex.value = -1;
};
const onChartKey = e => {
  const n = xTotal.value;
  if (!n) return;
  if (e.key === 'ArrowLeft') {
    hoverIndex.value = Math.max(0, (hoverIndex.value < 0 ? n : hoverIndex.value) - 1);
    e.preventDefault();
  } else if (e.key === 'ArrowRight') {
    hoverIndex.value = Math.min(n - 1, hoverIndex.value + 1);
    e.preventDefault();
  }
};
// 悬停数据: 本月点(索引超出本月已过天数时无值) + 上月点 + 十字线定位
const hoverPoint = computed(() => {
  const i = hoverIndex.value;
  if (i < 0 || !xTotal.value) return null;
  const cur = chartData.value[i];
  const prev = prevData.value[i];
  if (!cur && !prev) return null;
  // 十字线 y 取本月值, 本月没到该天时取上月值
  const anchor = cur || prev;
  return {
    day: i + 1,
    cur: cur || null,
    prev: prev || null,
    // 日环比: 本月第 i 天 vs 前一天(1号的前一天=上月最后一天, 用上月曲线兜底)
    delta: dayDelta(i),
    x: xAt(i),
    y: yAt(anchor.count),
  };
});
// 提示框水平定位(%), 左右边缘夹住避免溢出
const tooltipLeft = computed(() => {
  if (!hoverPoint.value) return 0;
  return Math.min(88, Math.max(12, (hoverPoint.value.x / CHART_W) * 100));
});
const tooltipTop = computed(() => {
  if (!hoverPoint.value) return 0;
  return (hoverPoint.value.y / CHART_H) * 100;
});

onMounted(fetchReport);
</script>

<template>
  <div class="overflow-auto bg-n-surface-1 w-full px-6">
    <div class="max-w-5xl mx-auto py-6 space-y-4">
      <!-- 标题 -->
      <div class="flex items-end justify-between">
        <div>
          <h1 class="text-2xl font-semibold text-n-slate-12">留电来源报表</h1>
          <p class="text-sm text-n-slate-10 mt-0.5">
            按归属者 × 来源URL 统计已留联客户数量
          </p>
        </div>
        <span
          v-if="loading"
          class="text-xs text-n-slate-10 pb-1.5 shrink-0"
        >查询中…</span>
      </div>

      <!-- 筛选条: 日期在前, 三个下拉多选一行排开(替代原来三行 chips 平铺) -->
      <div class="bg-n-solid-3 rounded-xl p-3 flex flex-wrap items-center gap-2">
        <WootDatePicker
          v-model:date-range="customDateRange"
          v-model:range-type="selectedRangeType"
          @date-range-changed="onDateRange"
        />
        <FilterDropdown
          label="归属者"
          :options="ownerOpts"
          :model-value="selectedOwners"
          @update:model-value="onOwnersChange"
        />
        <FilterDropdown
          label="来源"
          :options="sourceOpts"
          :model-value="selectedSources"
          @update:model-value="onSourcesChange"
        />
        <FilterDropdown
          label="网站域名"
          :options="domainOpts"
          :model-value="selectedDomains"
          search-placeholder="搜索域名…"
          @update:model-value="onDomainsChange"
        />
      </div>

      <div
        v-if="!loaded"
        class="bg-n-solid-3 rounded-xl py-24 text-center text-n-slate-10"
      >
        加载中...
      </div>

      <template v-else>
        <!-- 汇总: 刷新时保持原有渲染仅降透明度, 不闪骨架屏 -->
        <div
          class="grid grid-cols-2 sm:grid-cols-4 gap-3 transition-opacity"
          :class="loading ? 'opacity-50' : ''"
        >
          <div class="bg-n-blue-4/20 rounded-xl p-4 border border-n-blue-5/30">
            <div class="text-xs text-n-blue-9 mb-1">留电总数</div>
            <div class="text-2xl font-semibold text-n-blue-11">
              {{ total.toLocaleString() }}
            </div>
          </div>
          <div class="bg-n-solid-3 rounded-xl p-4">
            <div class="text-xs text-n-slate-10 mb-1">{{ monthLabel || '当月' }}留电</div>
            <div class="text-2xl font-semibold text-n-slate-12">
              {{ monthTotal.toLocaleString() }}
            </div>
          </div>
          <div class="bg-n-solid-3 rounded-xl p-4">
            <div class="text-xs text-n-slate-10 mb-1">当月日均</div>
            <div class="text-2xl font-semibold text-n-slate-12">
              {{ monthDailyAvg.toLocaleString() }}
            </div>
          </div>
          <div class="bg-n-solid-3 rounded-xl p-4">
            <div class="text-xs text-n-slate-10 mb-1">对话总数</div>
            <div class="text-2xl font-semibold text-n-slate-12">
              {{ conversationsTotal.toLocaleString() }}
            </div>
          </div>
        </div>

        <!-- 每日留电趋势(本月实线品牌色 + 上月虚线琥珀色按"日"对齐; 悬停十字线+双月数值) -->
        <div
          class="bg-n-solid-3 rounded-xl p-4 transition-opacity"
          :class="loading ? 'opacity-50' : ''"
        >
          <div class="mb-2 flex items-end justify-between flex-wrap gap-1">
            <div>
              <h2 class="text-sm font-semibold text-n-slate-12">每日留电趋势</h2>
              <p class="text-xs text-n-slate-9 mt-0.5">
                按留电时间(北京时区)逐日统计, 随归属者/来源/域名筛选联动, 不受日期范围影响
              </p>
            </div>
            <!-- 图例: 本月实线(品牌色) vs 上月虚线(琥珀色, 原始hex避开调色板缺色坑) -->
            <div class="flex items-center gap-3 text-xs text-n-slate-10 pb-0.5">
              <span class="flex items-center gap-1">
                <svg width="16" height="8" aria-hidden="true">
                  <line
                    x1="0"
                    y1="4"
                    x2="16"
                    y2="4"
                    class="stroke-n-brand"
                    stroke-width="2"
                  />
                </svg>
                {{ monthLabel || '本月' }} {{ monthTotal.toLocaleString() }}
              </span>
              <span class="flex items-center gap-1">
                <svg width="16" height="8" aria-hidden="true">
                  <line
                    x1="0"
                    y1="4"
                    x2="16"
                    y2="4"
                    stroke="#d97706"
                    stroke-width="2"
                    stroke-dasharray="4 3"
                  />
                </svg>
                {{ prevMonthLabel || '上月' }} {{ prevMonthTotal.toLocaleString() }}
              </span>
              <!-- 最新一天日环比(当天进行中, 仅供参考) -->
              <span
                v-if="latestDelta"
                class="flex items-center gap-1 border-l border-n-slate-5 pl-3 tabular-nums"
                :style="{ color: latestDelta.diff >= 0 ? '#16a34a' : '#dc2626' }"
              >
                {{ chartData[chartData.length - 1].day }}日较前日
                {{ fmtDelta(latestDelta) }}
              </span>
            </div>
          </div>
          <div class="relative">
            <svg
              :viewBox="`0 0 ${CHART_W} ${CHART_H}`"
              class="w-full h-auto block"
              role="img"
              :aria-label="`每日留电趋势, ${monthLabel}共${monthTotal}条, ${prevMonthLabel}共${prevMonthTotal}条`"
              tabindex="0"
              @mousemove="onChartMove"
              @mouseleave="onChartLeave"
              @keydown="onChartKey"
            >
              <!-- 网格线 + y 轴刻度(细线, 退后) -->
              <line
                v-for="t in yTicks"
                :key="t.y"
                x1="40"
                :y1="t.y"
                :x2="CHART_W - 16"
                :y2="t.y"
                class="stroke-n-slate-4"
                stroke-width="1"
              />
              <text
                v-for="t in yTicks"
                :key="`label-${t.y}`"
                x="34"
                :y="t.y + 3"
                text-anchor="end"
                class="fill-n-slate-9 tabular-nums"
                font-size="10"
              >{{ t.label }}</text>
              <!-- x 轴日期标签(稀疏, 按两月总天数铺开) -->
              <template v-for="i in xTotal" :key="`x-${i}`">
                <text
                  v-if="(i - 1) % xLabelStep === 0 || i === xTotal"
                  :x="xAt(i - 1)"
                  :y="CHART_H - 8"
                  text-anchor="middle"
                  class="fill-n-slate-9 tabular-nums"
                  font-size="10"
                >{{ i }}</text>
              </template>
              <!-- 上月曲线: 虚线琥珀色(画在底层, 本月实线压在上面) -->
              <path
                v-if="prevLinePath"
                :d="prevLinePath"
                fill="none"
                stroke="#d97706"
                stroke-width="2"
                stroke-dasharray="5 4"
                stroke-linejoin="round"
                stroke-linecap="round"
              />
              <!-- 本月面积淡填充 + 2px 折线 -->
              <path
                :d="areaPath"
                class="fill-n-brand"
                fill-opacity="0.08"
              />
              <path
                :d="linePath"
                fill="none"
                class="stroke-n-brand"
                stroke-width="2"
                stroke-linejoin="round"
                stroke-linecap="round"
              />
              <!-- 端点(最新一天): 品牌色点 + 2px 表面色描圈 -->
              <circle
                v-if="chartData.length"
                :cx="xAt(chartData.length - 1)"
                :cy="yAt(chartData[chartData.length - 1].count)"
                r="6"
                class="fill-n-solid-3"
              />
              <circle
                v-if="chartData.length"
                :cx="xAt(chartData.length - 1)"
                :cy="yAt(chartData[chartData.length - 1].count)"
                r="4"
                class="fill-n-brand"
              />
              <!-- 悬停十字线 + 两月各自高亮点 -->
              <g v-if="hoverPoint">
                <line
                  :x1="hoverPoint.x"
                  :y1="PAD.t"
                  :x2="hoverPoint.x"
                  :y2="PAD.t + plotH"
                  class="stroke-n-slate-6"
                  stroke-width="1"
                />
                <circle
                  v-if="hoverPoint.prev"
                  :cx="hoverPoint.x"
                  :cy="yAt(hoverPoint.prev.count)"
                  r="4"
                  fill="#d97706"
                  class="stroke-n-solid-3"
                  stroke-width="2"
                />
                <circle
                  v-if="hoverPoint.cur"
                  :cx="hoverPoint.x"
                  :cy="yAt(hoverPoint.cur.count)"
                  r="4"
                  class="fill-n-brand stroke-n-solid-3"
                  stroke-width="2"
                />
              </g>
              <!-- 悬停命中区(整个绘图区, 大于折线本身) -->
              <rect
                :x="PAD.l"
                :y="PAD.t"
                :width="plotW"
                :height="plotH"
                fill="transparent"
              />
            </svg>
            <!-- 悬停数值框(本月/上月两行对比; 纯文本绑定防注入) -->
            <div
              v-if="hoverPoint"
              class="absolute pointer-events-none -translate-x-1/2 -translate-y-[130%] bg-n-solid-2 border border-n-slate-5 rounded-lg px-2.5 py-1.5 shadow-md whitespace-nowrap"
              :style="{ left: `${tooltipLeft}%`, top: `${tooltipTop}%` }"
            >
              <div class="text-xs text-n-slate-9 mb-0.5">{{ hoverPoint.day }}日</div>
              <div
                class="flex items-center gap-1.5 text-sm font-semibold text-n-slate-12 tabular-nums"
              >
                <span class="inline-block w-2.5 h-0.5 rounded bg-n-brand" />
                {{ monthLabel || '本月' }} {{ (hoverPoint.cur?.count ?? 0).toLocaleString() }}
              </div>
              <div
                class="flex items-center gap-1.5 text-sm font-medium tabular-nums"
                style="color: #d97706"
              >
                <span
                  class="inline-block w-2.5 h-0.5 rounded"
                  style="background: #d97706"
                />
                {{ prevMonthLabel || '上月' }} {{ (hoverPoint.prev?.count ?? 0).toLocaleString() }}
              </div>
              <!-- 日环比: 本月当天 vs 前一天(涨绿跌红, 原始hex避开调色板缺色坑) -->
              <div
                v-if="hoverPoint.delta"
                class="flex items-center gap-1.5 text-xs font-medium tabular-nums mt-0.5"
                :style="{ color: hoverPoint.delta.diff >= 0 ? '#16a34a' : '#dc2626' }"
              >
                较前日
                <span class="font-semibold">{{ fmtDelta(hoverPoint.delta) }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- 分时留电趋势: 今日 vs 昨日按小时对比(独立组件, 同筛选联动) -->
        <HourlyTrendChart
          title="分时留电趋势（今日 vs 昨日）"
          subtitle="按留电小时(北京时区)统计, 随归属者/来源/域名筛选联动, 不受日期范围影响; 今日曲线截至当前小时"
          :primary="todayHourly"
          :primary-total="todayHourlyTotal"
          :primary-date="todayDate"
          primary-label="今日"
          :compare="yesterdayHourly"
          :compare-total="yesterdayHourlyTotal"
          :compare-date="yesterdayDate"
          compare-label="昨日"
          delta-label="较昨日同时段"
        />

        <!-- 分时留电趋势: 今日 vs 上周同期(同组件复用, 同筛选联动) -->
        <HourlyTrendChart
          title="分时留电趋势（今日 vs 上周同期）"
          subtitle="按留电小时(北京时区)统计, 随归属者/来源/域名筛选联动, 不受日期范围影响; 今日曲线截至当前小时"
          :primary="todayHourly"
          :primary-total="todayHourlyTotal"
          :primary-date="todayDate"
          primary-label="今日"
          :compare="lastweekHourly"
          :compare-total="lastweekHourlyTotal"
          :compare-date="lastweekDate"
          compare-label="上周同期"
          delta-label="较上周同期"
        />

        <!-- 明细表: Tab 切换 按来源URL / 按网站域名, 避免两张长表上下堆叠 -->
        <div :class="loading ? 'opacity-50' : ''" class="transition-opacity">
          <div class="flex items-center gap-1 mb-3 border-b border-n-slate-4">
            <button
              class="px-3 py-1.5 text-sm border-b-2 -mb-px transition-colors"
              :class="
                activeTab === 'source'
                  ? 'border-n-brand text-n-slate-12 font-medium'
                  : 'border-transparent text-n-slate-10 hover:text-n-slate-11'
              "
              @click="activeTab = 'source'"
            >
              按来源URL
              <span class="text-xs text-n-slate-9">{{ rows.length }}</span>
            </button>
            <button
              class="px-3 py-1.5 text-sm border-b-2 -mb-px transition-colors"
              :class="
                activeTab === 'domain'
                  ? 'border-n-brand text-n-slate-12 font-medium'
                  : 'border-transparent text-n-slate-10 hover:text-n-slate-11'
              "
              @click="activeTab = 'domain'"
            >
              按网站域名
              <span class="text-xs text-n-slate-9">{{ domainRows.length }}</span>
            </button>
          </div>

          <!-- Tab 1: 按归属者 × 来源URL -->
          <div
            v-if="activeTab === 'source'"
            class="bg-n-solid-3 rounded-xl overflow-hidden"
          >
            <div
              v-if="!rows.length"
              class="py-16 text-center text-n-slate-10"
            >
              当前筛选条件下无留电数据
            </div>
            <table v-else class="w-full text-sm">
              <thead>
                <tr class="bg-n-slate-2 border-b border-n-slate-4 text-n-slate-11">
                  <th
                    class="px-4 py-2.5 text-left font-medium cursor-pointer select-none whitespace-nowrap"
                    @click="setSort('owner')"
                  >
                    归属者{{ sortArrow('owner') }}
                  </th>
                  <th
                    class="px-4 py-2.5 text-left font-medium cursor-pointer select-none whitespace-nowrap"
                    @click="setSort('source')"
                  >
                    来源{{ sortArrow('source') }}
                  </th>
                  <th
                    class="px-4 py-2.5 text-left font-medium cursor-pointer select-none"
                    @click="setSort('source_url')"
                  >
                    来源URL{{ sortArrow('source_url') }}
                  </th>
                  <th
                    class="px-4 py-2.5 text-right font-medium cursor-pointer select-none whitespace-nowrap"
                    @click="setSort('count')"
                  >
                    留电数{{ sortArrow('count') }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(row, i) in sortedRows"
                  :key="`${row.owner}-${row.source_url}-${i}`"
                  class="border-b border-n-slate-3 hover:bg-n-brand/5"
                >
                  <td class="px-4 py-2 text-n-slate-12 whitespace-nowrap">
                    {{ row.owner }}
                  </td>
                  <td class="px-4 py-2 text-n-slate-11 whitespace-nowrap">
                    {{ row.source_label || '未记录' }}
                  </td>
                  <td class="px-4 py-2 text-n-slate-11 max-w-[360px]">
                    <a
                      v-if="row.source_url"
                      :href="row.source_url"
                      target="_blank"
                      rel="noopener nofollow noreferrer"
                      :title="row.source_url"
                      class="text-n-brand hover:underline truncate inline-block max-w-full align-bottom"
                    >{{ row.source_url }}</a>
                    <span v-else class="text-n-slate-9">未记录</span>
                  </td>
                  <td class="px-4 py-2 text-right font-semibold text-n-slate-12 tabular-nums">
                    {{ row.count.toLocaleString() }}
                  </td>
                </tr>
              </tbody>
              <tfoot>
                <tr class="bg-n-slate-2 border-t-2 border-n-slate-5 font-semibold">
                  <td class="px-4 py-2.5 text-n-slate-12" colspan="3">合计</td>
                  <td class="px-4 py-2.5 text-right text-n-slate-12 tabular-nums">
                    {{ total.toLocaleString() }}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>

          <!-- Tab 2: 按网站域名(widget 嵌入页 host × 归属者; 与主表"来源"维度互补) -->
          <div
            v-else
            class="bg-n-solid-3 rounded-xl overflow-hidden"
          >
            <div
              v-if="!domainRows.length"
              class="py-16 text-center text-n-slate-10"
            >
              当前筛选条件下无域名数据
            </div>
            <table v-else class="w-full text-sm">
              <thead>
                <tr class="bg-n-slate-2 border-b border-n-slate-4 text-n-slate-11">
                  <th class="px-4 py-2.5 text-left font-medium whitespace-nowrap">
                    归属者
                  </th>
                  <th class="px-4 py-2.5 text-left font-medium">网站域名</th>
                  <th class="px-4 py-2.5 text-right font-medium whitespace-nowrap">
                    留电数
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(row, i) in domainRows"
                  :key="`${row.owner}-${row.domain}-${i}`"
                  class="border-b border-n-slate-3 hover:bg-n-brand/5"
                >
                  <td class="px-4 py-2 text-n-slate-12 whitespace-nowrap">
                    {{ row.owner }}
                  </td>
                  <td class="px-4 py-2 text-n-slate-11">{{ row.domain }}</td>
                  <td class="px-4 py-2 text-right font-semibold text-n-slate-12 tabular-nums">
                    {{ row.count.toLocaleString() }}
                  </td>
                </tr>
              </tbody>
              <tfoot>
                <tr class="bg-n-slate-2 border-t-2 border-n-slate-5 font-semibold">
                  <td class="px-4 py-2.5 text-n-slate-12" colspan="2">合计</td>
                  <td class="px-4 py-2.5 text-right text-n-slate-12 tabular-nums">
                    {{ domainTotal.toLocaleString() }}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>
